import type { OutputResult, Processor, ProcessorSpanPhase } from '..';
import type { MastraDBMessage, MessageList } from '../../agent';
import { isTransientSignalMessage } from '../../agent/signals';
import { noteThreadMessagesSaved } from '../../agent/thread-saves';
import { loadMessageHistory, parseMemoryRequestContext } from '../../memory';
import { getMemoryTokenBoundary, isAfterMemoryTokenBoundary } from '../../memory/message-history-config';
import {
  removeWorkingMemoryTags,
  removeWorkingMemoryToolInvocationParts,
  removeWorkingMemoryToolInvocations,
} from '../../memory/working-memory-utils';
import { SpanType } from '../../observability';
import type { ObservabilityContext, MemoryOperationAttributes } from '../../observability';
import type { RequestContext } from '../../request-context';
import type { MemoryStorage } from '../../storage';

/**
 * Options for the MessageHistory processor
 */
export interface MessageHistoryOptions {
  storage: MemoryStorage;
  lastMessages?: number | false;
  tokenLimit?: { maxTokens: number; atMaxRemoveTokens: number };
  tokenCounter?: { countMessage(message: MastraDBMessage): number | Promise<number> };
}

/**
 * Hybrid processor that handles both retrieval and persistence of message history.
 * - On input: Fetches historical messages from storage and prepends them
 * - On output: Persists new messages to storage (excluding system messages)
 *
 * This processor retrieves threadId and resourceId from RequestContext at execution time,
 * making it decoupled from memory-specific context.
 */
/**
 * Which memory operation each pipeline phase performs. The input phase recalls
 * stored history into the context; the output phase saves the turn.
 */
const MEMORY_PHASE_OPERATION: Partial<Record<ProcessorSpanPhase, 'recall' | 'save'>> = {
  input: 'recall',
  inputStep: 'recall',
  output: 'save',
  outputStep: 'save',
};

export class MessageHistory implements Processor {
  readonly id = 'message-history';
  readonly name = 'MessageHistory';

  /**
   * Trace as a memory operation rather than an anonymous processor run: a user
   * configures `memory`, not a processor. The two phases are different memory
   * operations — the input phase recalls stored history, the output phase saves
   * the turn — so each is named for what it does.
   *
   * This replaces a MEMORY_OPERATION span the processor used to create *inside*
   * its own processor span, which meant two spans per phase describing one
   * operation.
   */
  readonly spanType = SpanType.MEMORY_OPERATION;
  readonly spanName = (phase: ProcessorSpanPhase): string => `memory: ${MEMORY_PHASE_OPERATION[phase] ?? 'recall'}`;
  readonly spanAttributes = (phase: ProcessorSpanPhase): Partial<MemoryOperationAttributes> => ({
    operationType: MEMORY_PHASE_OPERATION[phase] ?? 'recall',
  });
  private storage: MemoryStorage;
  private lastMessages?: number | false;
  private tokenLimit?: MessageHistoryOptions['tokenLimit'];
  private tokenCounter?: MessageHistoryOptions['tokenCounter'];

  constructor(options: MessageHistoryOptions) {
    this.storage = options.storage;
    this.lastMessages = options.lastMessages;
    this.tokenLimit = options.tokenLimit;
    this.tokenCounter = options.tokenCounter;
  }

  /**
   * Get threadId and resourceId from either RequestContext or MessageList's memoryInfo
   */
  private getMemoryContext(
    requestContext: RequestContext | undefined,
    messageList: MessageList,
  ): { threadId: string; resourceId?: string } | null {
    // First try RequestContext (set by Memory class)
    const memoryContext = parseMemoryRequestContext(requestContext);
    if (memoryContext?.thread?.id) {
      return {
        threadId: memoryContext.thread.id,
        resourceId: memoryContext.resourceId,
      };
    }

    // Fallback to MessageList's memoryInfo (set when MessageList is created with threadId)
    const serialized = messageList.serialize();
    if (serialized.memoryInfo?.threadId) {
      return {
        threadId: serialized.memoryInfo.threadId,
        resourceId: serialized.memoryInfo.resourceId,
      };
    }

    return null;
  }

  /**
   * This processor's own span, which the runner already typed as the memory
   * operation. Recording onto it rather than creating a child keeps one span
   * per memory operation. The runner owns its lifecycle, so this only ever
   * updates — it never ends or errors the span.
   */
  private memorySpan(observabilityContext?: Partial<ObservabilityContext>) {
    return observabilityContext?.tracingContext?.currentSpan;
  }

  async processInput(
    args: {
      messages: MastraDBMessage[];
      messageList: MessageList;
      abort: (reason?: string) => never;
      requestContext?: RequestContext;
    } & Partial<ObservabilityContext>,
  ): Promise<MessageList | MastraDBMessage[]> {
    const { messageList, requestContext, ...observabilityContext } = args;

    // Get memory context from RequestContext or MessageList
    const context = this.getMemoryContext(requestContext, messageList);

    if (!context) {
      return messageList;
    }

    const { threadId, resourceId } = context;
    const memoryRunState = parseMemoryRequestContext(requestContext)?.runState?.();

    const span = this.memorySpan(observabilityContext);
    span?.update({ attributes: { lastMessages: this.lastMessages } });

    try {
      // 1. Fetch historical messages from storage (as DB format)
      const storedBoundary = getMemoryTokenBoundary(parseMemoryRequestContext(requestContext)?.thread);
      const boundary =
        this.tokenLimit &&
        storedBoundary?.maxTokens === this.tokenLimit.maxTokens &&
        storedBoundary.atMaxRemoveTokens === this.tokenLimit.atMaxRemoveTokens
          ? storedBoundary
          : undefined;
      const cacheKey = `history:${threadId}:${resourceId ?? ''}:${this.lastMessages ?? 'all'}:${JSON.stringify(boundary)}`;
      const loadMessages = async () => {
        if (this.tokenLimit) {
          const result = await loadMessageHistory({
            storage: this.storage,
            threadId,
            resourceId,
            boundary,
            maxMessages: typeof this.lastMessages === 'number' ? this.lastMessages : undefined,
            maxTokens: this.tokenLimit.maxTokens,
            atMaxRemoveTokens: this.tokenLimit.atMaxRemoveTokens,
            tokenCounter: this.tokenCounter,
            includeOverflow: true,
          });
          return [...result.overflow, ...result.messages].reverse();
        }

        const result = await this.storage.listMessages({
          threadId,
          resourceId,
          page: 0,
          perPage: this.lastMessages,
          orderBy: { field: 'createdAt', direction: 'DESC' },
          // Last-N history read only consumes `messages`; skip the COUNT(*) work.
          includeTotal: false,
        });
        return result.messages;
      };
      const messages = memoryRunState ? await memoryRunState.load(cacheKey, loadMessages) : await loadMessages();

      // 2. Filter out system messages (they should never be stored in DB)
      const filteredMessages = messages.filter((msg: MastraDBMessage) => {
        return msg.role !== 'system' && (!boundary || isAfterMemoryTokenBoundary(msg, boundary));
      });

      // 3. Add stored history in chronological order. MessageList layers any matching
      // input copy onto the stored message so memory remains the authoritative base.
      const chronologicalMessages = filteredMessages.reverse();

      for (const msg of chronologicalMessages) {
        messageList.add(msg, 'memory');
      }

      span?.update({ attributes: { messageCount: chronologicalMessages.length } });

      return messageList;
    } catch (error) {
      // The runner records the failure on this span and ends it.
      throw error;
    }
  }

  /**
   * Filters messages before persisting to storage:
   * 1. Removes system messages - these are runtime instructions and should never be stored
   * 2. Removes transient signals (`transient: true`) - delivery-only, must never be retained
   * 3. Removes streaming tool calls (state === 'partial-call') - these are intermediate states
   * 4. Removes updateWorkingMemory tool invocations (hide args from message history)
   * 5. Strips <working_memory> tags from text content
   *
   * Note: We preserve 'call' state tool invocations because:
   * - For server-side tools, 'call' should have been converted to 'result' by the time OUTPUT is processed
   * - For client-side tools (no execute function), 'call' is the final state from the server's perspective
   */
  private filterMessagesForPersistence(messages: MastraDBMessage[]): MastraDBMessage[] {
    return messages
      .filter(m => m.role !== 'system' && !isTransientSignalMessage(m))
      .map(m => {
        const newMessage = { ...m };
        // Only spread content if it's a proper V2 object
        if (m.content && typeof m.content === 'object' && !Array.isArray(m.content)) {
          newMessage.content = { ...m.content };
        }

        // Strip working memory tags from string content
        if (typeof newMessage.content?.content === 'string' && newMessage.content.content.length > 0) {
          const cleanedContent = removeWorkingMemoryTags(newMessage.content.content);
          newMessage.content.content =
            cleanedContent !== newMessage.content.content ? cleanedContent.trim() : newMessage.content.content;
        }

        if (Array.isArray(newMessage.content?.parts)) {
          if (Array.isArray(newMessage.content.toolInvocations)) {
            newMessage.content.toolInvocations = removeWorkingMemoryToolInvocations(newMessage.content.toolInvocations);
          }
          // Filter out updateWorkingMemory tool invocations (hide args from message history)
          newMessage.content.parts = removeWorkingMemoryToolInvocationParts(newMessage.content.parts)
            .map(p => {
              // Filter out streaming tool calls (partial-call is an intermediate state during streaming)
              if (p.type === `tool-invocation` && p.toolInvocation.state === `partial-call`) {
                return null;
              }
              // Strip working memory tags from text parts
              if (p.type === `text`) {
                const text = typeof p.text === 'string' ? p.text : '';
                const cleaned = removeWorkingMemoryTags(text);
                return {
                  ...p,
                  text: cleaned !== text ? cleaned.trim() : text,
                };
              }
              return p;
            })
            .filter((p): p is NonNullable<typeof p> => Boolean(p));

          // If all parts were filtered out, skip the whole message
          if (newMessage.content.parts.length === 0) {
            return null;
          }
        }

        return newMessage;
      })
      .filter((m): m is NonNullable<typeof m> => Boolean(m));
  }

  async processOutputResult(
    args: {
      messages: MastraDBMessage[];
      messageList: MessageList;
      abort: (reason?: string) => never;
      requestContext?: RequestContext;
      result?: OutputResult;
    } & Partial<ObservabilityContext>,
  ): Promise<MessageList> {
    const { messageList, requestContext, result, ...observabilityContext } = args;

    // Get memory context from RequestContext or MessageList
    const context = this.getMemoryContext(requestContext, messageList);

    // Check if readOnly from memoryConfig
    const memoryContext = parseMemoryRequestContext(requestContext);
    const readOnly = memoryContext?.memoryConfig?.readOnly;

    if (!context || readOnly) {
      return messageList;
    }

    const { threadId, resourceId } = context;

    const newInput = messageList.get.input.db();
    const newOutput = messageList.get.response.db();
    // Apply transcript redaction before persisting: this path bypasses
    // drainUnsavedMessages(), and a background tool result may sit in the
    // list as a raw payload whose transcript transform lives only in
    // providerMetadata. Persisting it untransformed would leak the raw
    // payload to storage whenever this save lands after the redacting
    // save-queue flush (last-writer-wins on the message id).
    const messagesToSave = messageList.transformMessagesForTranscript([...newInput, ...newOutput]);

    if (messagesToSave.length === 0) {
      return messageList;
    }

    // Don't persist an input-only failed turn: if the provider errored before
    // producing any output, saving the user message would orphan it in history.
    if (result?.finishReason === 'error' && newOutput.length === 0) {
      return messageList;
    }

    const span = this.memorySpan(observabilityContext);
    span?.update({ attributes: { messageCount: messagesToSave.length } });

    try {
      await this.persistMessages({ messages: messagesToSave, threadId, resourceId });
      // add extra 1ms latency to make sure the next generate has not the same input
      await new Promise(resolve => setTimeout(resolve, 10));

      return messageList;
    } catch (error) {
      // The runner records the failure on this span and ends it.
      throw error;
    }
  }

  /**
   * Persist messages to storage, filtering out partial tool calls and working memory tags.
   * Also ensures the thread exists (creates if needed).
   *
   * This method can be called externally by other processors (e.g., ObservationalMemory)
   * that need to save messages incrementally.
   */
  async persistMessages(args: { messages: MastraDBMessage[]; threadId: string; resourceId?: string }): Promise<void> {
    const { messages, threadId, resourceId } = args;

    if (messages.length === 0) {
      return;
    }

    const filtered = this.filterMessagesForPersistence(messages);

    if (filtered.length === 0) {
      return;
    }

    const savedAt = Date.now();

    // Ensure thread exists (create if needed) before saving messages.
    // Nothing to write when it already exists: re-writing the row we just read
    // would clobber a title generated concurrently with this save.
    const thread = await this.storage.getThreadById({ threadId });
    if (!thread) {
      // Auto-create thread if it doesn't exist
      await this.storage.saveThread({
        thread: {
          id: threadId,
          resourceId: resourceId || threadId,
          title: '',
          metadata: {},
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      });
    }

    // Persist messages after thread is guaranteed to exist
    await this.storage.saveMessages({ messages: filtered });
    noteThreadMessagesSaved({ threadId, resourceId, savedAt });
  }
}
